import type { PlanDefinition, PlanDefinitionAction } from '@medplum/fhirtypes';
import { PROTOCOL_DELIVERY_MODES, PROTOCOL_EXTENSIONS, type ProtocolDeliveryMode } from '../extensions';

/**
 * The leaf actions of a PlanDefinition — the ones that materialize — flattened out of their groups.
 * A group (an action with nested `action`s) is structure, not work: it names a section of the protocol,
 * and only its leaves become items.
 *
 * Generic over any PlanDefinition, so it lives with the engine rather than with one protocol's
 * definition; every protocol and every reader of a protocol shares this one walk.
 */
export const leafActions = (planDefinition: PlanDefinition): PlanDefinitionAction[] => {
  const walk = (actions: PlanDefinitionAction[]): PlanDefinitionAction[] =>
    actions.flatMap((a) => (a.action?.length ? walk(a.action) : [a]));
  return walk(planDefinition.action ?? []);
};

/**
 * How this protocol's actions reach the care plan, from the `protocol-delivery` extension on the
 * definition. Defaults to `commit`, so a definition written before the marker existed keeps behaving
 * as it did. An unrecognized value is treated as `commit` too — the same conservative reading, and
 * the definition tests pin the marker on each protocol that needs one.
 */
export const deliveryModeOf = (planDefinition: PlanDefinition): ProtocolDeliveryMode => {
  const value = planDefinition.extension?.find((e) => e.url === PROTOCOL_EXTENSIONS.DELIVERY)?.valueCode;
  return (PROTOCOL_DELIVERY_MODES as readonly string[]).includes(value ?? '')
    ? (value as ProtocolDeliveryMode)
    : 'commit';
};
